import { WidgetComponent } from "../widget.types";
import React, { useContext, useEffect, useState } from "react";
import { MessageWithActionAndData, useMessages } from "@me8eon/sessions_messages";
import { AiClient } from "@me8eon/aiclient";
import { handleAiQueryNew, QuickActions } from "../../../components/ai-assist-refactored/quick-actions/quickActions.component";
import { getUserCommand } from "../../../components/ai-assist-refactored/quick-actions/decisionTree";
import { azureOpenAiClient, defaultAzureAiConfig } from "@me8eon/azureai";
import axios from "axios";
import { EnvironmentConfig, INCIDENT_LLM_MODEL } from "@me8eon/config";
import { isErrors } from "@me8eon/errors";
import { useJwtToken } from "@me8eon/react_authentication";
import useSearchStore from "../../../store/searchStore";
import LiveAgentChat from "../../../components/live-agent-chat/LiveAgentChat";

let liveAgentWidgetSequence = 0;
let latestLiveAgentWidgetSequence = 0;

export const LiveAgentChatActionWidget: WidgetComponent = (props) => {

    const values = props.values._meta.widget as any;

    const jwt = useJwtToken()
    const baseAiConfig = defaultAzureAiConfig(axios, `${EnvironmentConfig.gatewayURL}/proxy`, jwt);
    const aiClient = azureOpenAiClient({
        ...baseAiConfig,
        customisation: {
            ...baseAiConfig.customisation,
            model: INCIDENT_LLM_MODEL,
        },
    });
    const [messages] = useMessages();
    const query = getUserCommand("chatWithServiceDeskNew");
    const firstNode = handleAiQueryNew(messages, query, "chatWithServiceDesk", null)?.data;
    const [nodes, setNodes] = useState<any[]>([firstNode]);
    const { showLiveAgentChat, setShowLiveAgentChat } = useSearchStore();
    const [incidentText, setIncidentText] = useState<string>("");
    const [isIncidentTextPending, setIsIncidentTextPending] = useState(true);
    const widgetSequenceRef = React.useRef<number>(0);

    if (widgetSequenceRef.current === 0) {
        liveAgentWidgetSequence += 1;
        widgetSequenceRef.current = liveAgentWidgetSequence;
        latestLiveAgentWidgetSequence = liveAgentWidgetSequence;
    }

    const isLatestWidgetInstance = widgetSequenceRef.current === latestLiveAgentWidgetSequence;

    useEffect(() => {
        setShowLiveAgentChat(false);
        handleCreateSummaryForIncident();
    }, []);

    const handleAction = (option: any) => {
        let userCommand = getUserCommand(option.nextNodeId);
        let node = handleAiQueryNew(messages, userCommand, "chatWithServiceDesk", null)?.data;
        node = { ...node, callback: handleAction, setIncidentText };
        if (option.data) {
            node = { ...node, ...option.data }
        }
        setNodes((prev: any[]) => [
            ...prev,
            node
        ]);
    };

    const filterOnlyAssistantOrUserMessages = (messages: MessageWithActionAndData[]) => {
        return messages.filter(m => m.role === "user" || m.role === "assistant");
    }

    const handleCreateSummaryForIncident = async () => {
        const incidentDescription = await summarizeAllMessagesToIncidentDescription(messages, aiClient);
        if (incidentDescription) {
            setIncidentText(incidentDescription);
            setIsIncidentTextPending(false);
        }
    };

    const summarizeAllMessagesToIncidentDescription = async (messages: MessageWithActionAndData[], client: AiClient) => {

        const lastUserMessage = [...messages].reverse().find(m => m.role === "user")?.content || "";

        const systemPrompt = `You are an AI assistant that helps create concise and clear incident descriptions for ServiceNow based on user-reported issues. Summarize the following conversation into a brief description suitable for a ServiceNow incident ticket. Focus on the key problem, affected systems, and any relevant details provided by the user.
        
        You must respond in the **same language** the user used in their latest message '${lastUserMessage}'. If the user asks in German, answer in German. If in Polish, answer in Polish. If in English, answer in English.

        Example Output:
        
        I have issues accessing my email on Outlook. It shows an error code 500 when I try to log in. This has been happening since yesterday afternoon. My account is critical for daily operations, and I need urgent assistance to resolve this issue.`;

        const conversation = filterOnlyAssistantOrUserMessages(messages).map(m => `${m.role === "user" ? "User" : "Assistant"}: ${m.content}`).join("\n");
        const prompt = `${systemPrompt}\n\nConversation:\n${conversation}\n\nIncident Description:`;
        const aiResult = await client([
            { role: "system", content: systemPrompt },
            { role: "user", content: prompt }
        ]);
        if (isErrors(aiResult)) return;

        const messageContent = aiResult.value.message.content;
        if (typeof messageContent === "string") return messageContent;
        if (Array.isArray(messageContent)) {
            return messageContent
                .map((part: any) => (part && typeof part === "object" && "text" in part ? part.text : ""))
                .join(" ")
                .trim();
        }

        return "";
    };


    return (
        <div className="live-agent-chat-widget ai-assist-wrapper" style={{ flexDirection: "column", minHeight: "auto", padding: 0, margin: 0, gap: 0 }}>
            {nodes.map((node, index) => (
                <QuickActions
                    key={index}
                    role="assistant"
                    toolCalls={[]}
                    data={{ ...node, callback: handleAction, incidentText, isIncidentTextPending, setIncidentText }}
                    content={node.systemAnswer}
                />
            ))}
            {showLiveAgentChat && isLatestWidgetInstance ? <LiveAgentChat data={{ incidentText, isIncidentTextPending }} /> : <></>}
        </div>
    );
}